import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { Receipt, X, Camera, Upload, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { useOutletContext } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/mileage`;

const Receipts = () => {
  const [allReceipts, setAllReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enlargedReceipt, setEnlargedReceipt] = useState(null);
  const [showReceiptOptions, setShowReceiptOptions] = useState(false);
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('current'); // 'current', 'all', or 'YYYY-MM'
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Get camera trigger from layout
  const layoutContext = useOutletContext();
  
  // Listen for camera button clicks from layout
  useEffect(() => {
    if (layoutContext?.showReceiptUpload) {
      setShowReceiptOptions(true);
      layoutContext.setShowReceiptUpload(false);
    }
  }, [layoutContext?.showReceiptUpload]);

  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // getMonth() is 0-indexed
    return `${year}-${month}`;
  };

  useEffect(() => {
    fetchReceipts();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterDropdown]);

  const fetchReceipts = async () => {
    try {
      const response = await api.get(`${API}/receipts`);
      // Sort receipts by date (newest first)
      const sorted = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setAllReceipts(sorted);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      toast.error('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  };

  // Get unique months from receipts
  const getAvailableMonths = () => {
    const months = [...new Set(allReceipts.map(r => r.date.substring(0, 7)))];
    return months.sort().reverse();
  };

  // Filter receipts based on selected filter
  const getFilteredReceipts = () => {
    if (selectedFilter === 'all') {
      return allReceipts;
    }
    
    const targetMonth = selectedFilter === 'current' ? getCurrentMonth() : selectedFilter;
    return allReceipts.filter(receipt => receipt.date.startsWith(targetMonth));
  };

  const filteredReceipts = getFilteredReceipts();
  const availableMonths = getAvailableMonths();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-[#14B8A6] text-xl">Loading...</div>
      </div>
    );
  }

  // Get filter display text
  const getFilterDisplayText = () => {
    if (selectedFilter === 'all') return 'All Receipts';
    if (selectedFilter === 'current') {
      const monthDate = new Date(getCurrentMonth() + '-01');
      return format(monthDate, 'MMMM yyyy');
    }
    const monthDate = new Date(selectedFilter + '-01');
    return format(monthDate, 'MMMM yyyy');
  };

  return (
    <div className="space-y-4 md:space-y-6 fade-in pb-24">
      <div className="flex items-center justify-between gap-4">
        <p className="text-gray-400 text-sm md:text-base">Your fuel receipts</p>
        
        {/* Month Filter Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="glass-card px-4 py-2 flex items-center gap-2 hover:border-[#14B8A6] transition-colors"
            data-testid="month-filter-dropdown"
          >
            <span className="text-sm font-medium">{getFilterDisplayText()}</span>
            <ChevronDown className="w-4 h-4" />
          </button>

          {showFilterDropdown && (
            <div className="absolute right-0 mt-2 w-56 glass-card p-2 z-50">
              <button
                onClick={() => {
                  setSelectedFilter('current');
                  setShowFilterDropdown(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg hover:bg-[rgba(20,184,166,0.2)] transition-colors ${
                  selectedFilter === 'current' ? 'bg-[rgba(20,184,166,0.2)] text-[#14B8A6]' : ''
                }`}
              >
                Current Month
              </button>
              
              <button
                onClick={() => {
                  setSelectedFilter('all');
                  setShowFilterDropdown(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg hover:bg-[rgba(20,184,166,0.2)] transition-colors ${
                  selectedFilter === 'all' ? 'bg-[rgba(20,184,166,0.2)] text-[#14B8A6]' : ''
                }`}
              >
                View All
              </button>

              {availableMonths.length > 0 && (
                <>
                  <div className="border-t border-[rgba(20,184,166,0.2)] my-2" />
                  <div className="max-h-48 overflow-y-auto">
                    {availableMonths.map((month) => {
                      const monthDate = new Date(month + '-01');
                      return (
                        <button
                          key={month}
                          onClick={() => {
                            setSelectedFilter(month);
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-lg hover:bg-[rgba(20,184,166,0.2)] transition-colors text-sm ${
                            selectedFilter === month ? 'bg-[rgba(20,184,166,0.2)] text-[#14B8A6]' : ''
                          }`}
                        >
                          {format(monthDate, 'MMMM yyyy')}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {filteredReceipts.length === 0 ? (
        <div className="glass-card p-8 md:p-12 text-center">
          <Receipt className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-base md:text-lg">No receipts found</p>
          <p className="text-gray-500 text-xs md:text-sm mt-2">
            {selectedFilter === 'all' || selectedFilter === 'current' 
              ? 'Use the camera button below to upload fuel receipts'
              : 'Try selecting a different month or view all receipts'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:gap-6">
          {filteredReceipts.map((receipt) => (
            <button
              key={receipt.id}
              onClick={() => setEnlargedReceipt(receipt)}
              data-testid={`receipt-card-${receipt.id}`}
              className="glass-card p-6 hover:border-[#14B8A6] transition-all hover:scale-105 flex flex-col justify-between text-left"
              style={{ minHeight: '200px' }}
            >
              {/* Receipt Info - No Image */}
              <div className="space-y-3">
                {receipt.merchant && (
                  <p className="text-lg md:text-xl font-bold text-white">
                    {receipt.merchant}
                  </p>
                )}
                <p className="text-sm md:text-base text-[#14B8A6]">
                  {format(new Date(receipt.date), 'dd MMM yyyy')}
                </p>
              </div>
              
              <div className="mt-4">
                <p className="text-3xl md:text-4xl font-bold text-[#14B8A6]">
                  £{receipt.amount.toFixed(2)}
                </p>
                {receipt.notes && (
                  <p className="text-xs text-gray-400 mt-2 line-clamp-2">
                    {receipt.notes}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Enlarged Receipt Modal - Full Details */}
      {enlargedReceipt && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setEnlargedReceipt(null)}
        >
          <div 
            className="glass-card max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 glass-card p-4 md:p-6 flex items-center justify-between border-b border-[rgba(20,184,166,0.2)]">
              <h2 className="text-xl md:text-2xl font-bold">Receipt Details</h2>
              <button
                onClick={() => setEnlargedReceipt(null)}
                className="text-gray-400 hover:text-white transition-colors"
                data-testid="close-enlarged-receipt"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Receipt Image */}
                <div className="bg-[rgba(30,30,30,0.6)] rounded-xl overflow-hidden">
                  <img
                    src={enlargedReceipt.image_url}
                    alt="Receipt"
                    className="w-full h-auto object-contain"
                  />
                </div>

                {/* Receipt Details */}
                <div className="space-y-6">
                  {enlargedReceipt.merchant && (
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Merchant</p>
                      <p className="text-2xl font-bold text-white">{enlargedReceipt.merchant}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm text-gray-400 mb-1">Date</p>
                    <p className="text-xl text-[#14B8A6]">
                      {format(new Date(enlargedReceipt.date), 'dd MMMM yyyy')}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-400 mb-1">Amount</p>
                    <p className="text-4xl font-bold text-[#14B8A6]">
                      £{enlargedReceipt.amount.toFixed(2)}
                    </p>
                  </div>

                  {enlargedReceipt.notes && (
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Notes</p>
                      <p className="text-base text-gray-300">{enlargedReceipt.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Receipt Options Modal */}
      {showReceiptOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="glass-card p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Add Receipt</h2>
              <button
                onClick={() => setShowReceiptOptions(false)}
                data-testid="close-receipt-options"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => {
                  setShowReceiptOptions(false);
                  setShowReceiptUpload(true);
                }}
                data-testid="take-photo-option"
                className="glass-card p-6 hover:border-[#14B8A6] transition-all hover:scale-105 text-center"
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="p-4 bg-[rgba(20,184,166,0.2)] rounded-full">
                    <Camera className="w-8 h-8 text-[#14B8A6]" />
                  </div>
                  <div>
                    <p className="font-bold text-lg mb-1">Take Photo</p>
                    <p className="text-sm text-gray-400">Capture receipt with camera</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowReceiptOptions(false);
                  setShowReceiptUpload(true);
                }}
                data-testid="upload-photo-option"
                className="glass-card p-6 hover:border-[#14B8A6] transition-all hover:scale-105 text-center"
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="p-4 bg-[rgba(20,184,166,0.2)] rounded-full">
                    <Upload className="w-8 h-8 text-[#14B8A6]" />
                  </div>
                  <div>
                    <p className="font-bold text-lg mb-1">Upload Photo</p>
                    <p className="text-sm text-gray-400">Select receipt from gallery</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Receipt Upload Modal */}
      {showReceiptUpload && (
        <ReceiptUploadModal 
          onClose={() => {
            setShowReceiptUpload(false);
            fetchReceipts();
          }}
        />
      )}
    </div>
  );
};

// Receipt Upload Modal Component
const ReceiptUploadModal = ({ onClose }) => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState('');
  const [merchant, setMerchant] = useState('');
  const [notes, setNotes] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const scanReceipt = async (file) => {
    setScanning(true);
    toast.info('Scanning receipt...');
    
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result.split(',')[1];
          
          const response = await api.post(`${API}/receipts/scan`, {
            image_base64: base64,
          });
          
          // Fill in the extracted data
          if (response.data.merchant) setMerchant(response.data.merchant);
          if (response.data.date) setDate(response.data.date);
          if (response.data.amount) setAmount(response.data.amount.toString());
          
          toast.success('Receipt scanned! Please verify the information.');
        } catch (error) {
          console.error('Scan error:', error);
          toast.error('Failed to scan receipt. Please enter details manually.');
        } finally {
          setScanning(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Failed to read image');
      setScanning(false);
    }
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setImageFile(file);
      // Automatically scan the receipt
      scanReceipt(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg'] },
    multiple: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile || !amount) {
      toast.error('Please upload receipt and enter amount');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        await api.post(`${API}/receipts`, {
          date,
          amount: parseFloat(amount),
          merchant,
          notes,
          image_base64: base64,
        });
        
        toast.success('Receipt uploaded');
        onClose();
      };
      reader.readAsDataURL(imageFile);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="glass-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Upload Fuel Receipt</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Upload Area First */}
          <div>
            <label className="block text-sm font-medium mb-2">Receipt Image</label>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-6 transition-colors cursor-pointer ${
                isDragActive
                  ? 'border-[#14B8A6] bg-[rgba(20,184,166,0.1)]'
                  : imageFile
                  ? 'border-[#14B8A6]'
                  : 'border-[rgba(20,184,166,0.3)]'
              }`}
            >
              <input {...getInputProps()} />
              <div className="text-center">
                {scanning ? (
                  <>
                    <p className="text-[#14B8A6] font-medium mb-2">🔍 Scanning...</p>
                    <p className="text-sm text-gray-400">Please wait</p>
                  </>
                ) : imageFile ? (
                  <>
                    <p className="text-[#14B8A6] font-medium mb-2">✓ Receipt Scanned</p>
                    <p className="text-sm text-gray-400">{imageFile.name}</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium mb-2">Upload receipt image</p>
                    <p className="text-sm text-gray-400">Click or drag to upload</p>
                    <p className="text-xs text-gray-500 mt-2">Will auto-scan for details</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          {imageFile && !scanning && (
            <>
              <div className="pt-2">
                <p className="text-sm text-[#14B8A6] mb-3">✓ Review and edit extracted information:</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Merchant Name</label>
                <input
                  type="text"
                  value={merchant}
                  onChange={(e) => setMerchant(e.target.value)}
                  className="glass-input w-full text-sm"
                  placeholder="e.g. Shell, Tesco"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="glass-input w-full text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Amount (£)</label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="glass-input w-full text-sm"
                  placeholder="45.50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="glass-input w-full text-sm"
                  placeholder="Additional notes"
                />
              </div>

              <button
                type="submit"
                disabled={uploading}
                className="btn-primary w-full text-sm"
              >
                {uploading ? 'Saving...' : 'Save Receipt'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Receipts;
